import React from "react";
import { Box } from "../../components/ui/box";
import { Button, type ButtonProps } from "../../components/ui/button";
import { Kbd, KbdGroup } from "../../components/ui/kbd";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { Omittable } from "../../lib/props";

/**
 * ControlBar — the floating control card that hosts a cluster of view tools
 * over a canvas surface (the map's zoom/locate controls, the automation
 * diagram's fit/zoom/orientation clusters): an elevated rounded card whose
 * children are seamless ghost buttons, usually joined through `Group` with
 * explicit separators.
 *
 * Two surface treatments:
 * - Default (themed): elevated surface fill + hairline border — for canvases
 *   that share the app's theme (the automation diagram pane).
 * - `backgroundColor` (raw override): surfaces the CSS theme does not paint —
 *   Google Maps tiles — pass the pre-resolved color; the hairline border is
 *   dropped there (the elevation alone separates the card from imagery).
 *
 * `ControlBar`/`ControlBarButton` are an app-side composition over `Group` and
 * `Toolbar`, domain-free so both the map and the automation canvas share them.
 */
type ControlBarProps = Omittable<{
  children: React.ReactNode;
  /**
   * Raw background color for non-themed surfaces (e.g. the map's
   * `getMapColors().controlBg`). Omits the themed border when set.
   */
  backgroundColor?: string;
  /** Minimum height in px (the map's touch-sized bars use 40). */
  minHeight?: number;
}>;

export function ControlBar({
  children,
  backgroundColor,
  minHeight,
}: ControlBarProps) {
  // Children (ghost buttons) paint square hover washes edge-to-edge; the card
  // clips them to its own radius, so corners stay rounded without any
  // per-button radius.
  if (backgroundColor != null) {
    return (
      <Box
        align="center"
        className="shadow-lg/5"
        justify="center"
        minHeight={minHeight}
        overflow="hidden"
        rounded="lg"
        // The map's tiles are imagery, not a themed surface, so the card color
        // arrives pre-resolved from the Google map style rather than a token.
        style={{ backgroundColor }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      align="center"
      border
      borderColor="border"
      justify="center"
      minHeight={minHeight}
      // A floating cluster over the view it controls — its own surface.
      surface="popover"
      overflow="hidden"
      rounded="lg"
    >
      {children}
    </Box>
  );
}

/**
 * ControlBarButton — one ghost icon control inside a ControlBar, with a hover
 * tooltip naming the action (icon-only buttons never self-describe) and an
 * optional keycap hint beside the label. Plain Buttons since these clusters
 * don't own selection — their seamless look comes from explicit separators +
 * the rounded card.
 */
type ControlBarButtonProps = Omittable<{
  /** Tooltip text AND accessibility label — the action's name. */
  label: string;
  /**
   * Keys shown as a keycap hint beside the label — e.g. `['1']`,
   * `['cmd', 'z']`. Purely informational; the handlers live with the caller.
   */
  shortcut?: string[];
  onClick?: () => void;
  disabled?: boolean;
  /** Button size; the map's touch-sized controls use `icon-lg`. */
  size?: ButtonProps["size"];
  /** The icon (an `<Icon/>`, colored by the caller when needed). */
  children: React.ReactNode;
}>;

export function ControlBarButton({
  label,
  shortcut,
  onClick,
  disabled,
  size = "icon",
  children,
}: ControlBarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={label}
            // Square hover wash — the ControlBar card clips it to its radius.
            className="rounded-none before:rounded-none"
            disabled={disabled}
            onClick={onClick}
            size={size}
            variant="ghost"
          >
            {children}
          </Button>
        }
      />
      <TooltipPopup side="top">
        <Box align="center" direction="row" gap="xs">
          <Text size="xs">{label}</Text>
          {shortcut?.length ? (
            <KbdGroup>
              {shortcut.map((key) => (
                <Kbd key={key}>{key}</Kbd>
              ))}
            </KbdGroup>
          ) : null}
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}
